<template>
  <SectionFrame v-if="card" :section="section" :index="index" label="Back" narrow>
    <component :is="card.href ? 'a' : 'div'" :href="card.href || null" class="t-card t-link s2-back mo-reveal" :class="card.image ? '' : 's2-back--plain'">
      <span v-if="card.image" class="s2-back__media">
        <img :src="card.image" v-bind="imgAttrs(card.image, '(min-width: 860px) 50vw, 100vw')" :alt="card.imageAlt" class="t-photo s2-back__img" loading="lazy">
      </span>
      <span class="s2-back__body">
        <span class="t-eyebrow s2-back__eyebrow"><span class="s2-back__arrow" aria-hidden="true">←</span> {{ card.name ? "Back to" : "Back" }}</span>
        <span v-if="card.name" class="t-h3 s2-back__title">{{ card.name }}</span>
        <span v-if="card.description" class="t-body s2-muted s2-back__text">{{ card.description }}</span>
      </span>
    </component>
  </SectionFrame>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {imgAttrs} from "@/utils/photoSizes.js";
import {shownImageUrl} from "@/utils/sectionMedia.js";
import {safeBackPath} from "@/utils/returnLink.js";
import {groupLinkOf, resolveBack} from "@/utils/navTrail.js";
import {trail, trailReady} from "@/composables/useNavTrail.js";
import {usePageStore} from "@/stores/pageStore.js";
import {studioMode} from "@/classes/previewBridge.js";
import SectionFrame from "./SectionFrame.vue";

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}, data: {type: Object, default: () => ({})}});
const route = useRoute();
const pageStore = usePageStore();
const siteName = computed(() => String(pageStore.pageData?.companyName || "").trim());
const inStudio = computed(() => studioMode.value !== "off");

// The destination's current words and photo, asked of the server once per page view (the trail's copy may be from
// before the owner changed the page, or missing for a direct link with ?back= or a new tab).
// path -> {title, description, image, imageAlt} | false (no such page)
const asked = ref({});
const asking = new Set();
const prefix = import.meta.env.VITE_IS_PREVIEW_SERVER === "true" ? "/api/preview" : "";
async function ask(path) {
  if (asking.has(path)) return;
  asking.add(path);
  let got = false;
  try {
    const res = await fetch(`${prefix}/api/page-card?path=${encodeURIComponent(path)}`);
    if (res.ok) got = await res.json();
  } catch { /* offline: treated as unknown */ }
  asked.value = {...asked.value, [path]: got && got.title !== undefined ? got : false};
}

const strategy = computed(() => (["origin", "previous", "section"].includes(props.section.strategy) ? props.section.strategy : "origin"));
const fallbackPath = computed(() => safeBackPath(props.section.fallbackLink));
const queryBack = computed(() => (typeof route.query.back === "string" ? route.query.back : null));

const target = computed(() => {
  if (!trailReady.value) return null;
  const path = route.path;
  const own = [...trail.value].reverse().find((e) => e.path === path);
  const current = {path, from: own?.from ?? safeBackPath(queryBack.value), group: own?.group ?? null};
  return resolveBack({
    strategy: strategy.value,
    trail: trail.value,
    current,
    queryBack: queryBack.value,
    excludes: props.section.excludePaths,
    groupLink: groupLinkOf(pageStore.navData, path) || current.group,
  });
});

// The server's answer once it is in (false: the page is gone); until then the trail's copy, so the card shows at once.
const known = (p) => {
  if (asked.value[p] === false) return null;
  if (asked.value[p]) return asked.value[p];
  return target.value?.path === p && target.value.entry?.title ? target.value.entry : null;
};

// Ask the server for a destination (or fallback) the trail does not describe.
watch([target, fallbackPath, trailReady], () => {
  if (!trailReady.value) return;
  const t = target.value;
  if (t && asked.value[t.path] === undefined) ask(t.path);
  const f = fallbackPath.value;
  if (f && (!t || asked.value[t.path] === false) && !props.section.fallbackTitle && asked.value[f] === undefined) ask(f);
}, {immediate: true});

const fallbackImage = computed(() => shownImageUrl(props.section.fallbackImage));

const card = computed(() => {
  if (!trailReady.value) return null;
  const t = target.value;
  const make = (path, meta, usingFallback) => {
    const title = (usingFallback && String(props.section.fallbackTitle || "").trim()) || meta?.title || "";
    const image = (usingFallback && fallbackImage.value) || meta?.image || "";
    return {
      href: path,
      // Home is "Home": its share title is often the hero's words.
      name: path === "/" && !usingFallback ? "Home" : title,
      // A description that only repeats the name or the site name adds nothing.
      description: meta?.description && !String(meta?.title || "").startsWith(meta.description.trim()) && meta.description.trim() !== siteName.value ? meta.description : "",
      image,
      imageAlt: image && image === meta?.image ? meta.imageAlt || "" : "",
    };
  };
  if (t) {
    const meta = known(t.path);
    if (meta) return make(t.path, meta, t.path === fallbackPath.value);
    if (asked.value[t.path] === undefined) return placeholder();
  }
  // Nothing usable (or the page is gone): the fallback, else the home page, else nothing.
  const f = fallbackPath.value && fallbackPath.value !== route.path ? fallbackPath.value : null;
  if (f) {
    const meta = asked.value[f] || null;
    if (meta || props.section.fallbackTitle || fallbackImage.value) return make(f, meta, true);
    if (asked.value[f] === undefined) return placeholder();
    return make(f, null, true);
  }
  if (props.section.hideWhenUnavailable === false && route.path !== "/") {
    return {href: "/", name: String(props.section.fallbackTitle || "").trim() || "Home", description: "", image: fallbackImage.value, imageAlt: ""};
  }
  return placeholder();
});

// While a destination is still being looked up, or in the studio when there is none: nothing, or an example card.
function placeholder() {
  if (!inStudio.value) return null;
  return {href: null, name: "Your previous page", description: "Visitors see the page they came from here, with its photo and words.", image: fallbackImage.value, imageAlt: ""};
}
</script>

<style>
.s2-back { display: grid; grid-template-columns: minmax(0, 15rem) minmax(0, 1fr); gap: clamp(1rem, 3vw, 1.75rem); align-items: center; text-decoration: none; max-width: 44rem; margin-inline: auto; }
a.s2-back { cursor: pointer; }
.s2-back--plain { grid-template-columns: minmax(0, 1fr); }
.s2-back__media { display: block; overflow: hidden; border-radius: var(--t-photo-radius, 0); }
.s2-back__img { width: 100%; height: auto; aspect-ratio: 16 / 10; object-fit: cover; display: block; }
.s2-back__body { display: flex; flex-direction: column; gap: .35rem; min-width: 0; }
.s2-back__eyebrow { display: inline-flex; align-items: center; gap: .4rem; }
.s2-back__title { overflow-wrap: anywhere; font-size: clamp(1.15rem, 1rem + .6vw, 1.5rem); line-height: 1.25; margin: 0; }
.s2-back__text { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.s2-back__arrow { font-size: 1.1em; line-height: 1; color: var(--t-accent); }
a.s2-back:hover .s2-back__title { text-decoration: underline; }
a.s2-back:focus-visible { outline: 3px solid color-mix(in srgb, var(--t-accent) 55%, transparent); outline-offset: 3px; }
@media (max-width: 560px) { .s2-back { grid-template-columns: minmax(0, 1fr); } }
@media (prefers-reduced-motion: no-preference) {
  .s2-back__img { transition: transform .5s ease; }
  .s2-back__arrow { display: inline-block; transition: transform .25s ease; }
  a.s2-back:hover .s2-back__img { transform: scale(1.04); }
  a.s2-back:hover .s2-back__arrow, a.s2-back:focus-visible .s2-back__arrow { transform: translateX(-.25rem); }
}
</style>
